import {
  DiscountType,
  MEDICINE_DELIVERY_TYPE,
} from "graphql/types/globalTypes";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth } from "hooks/authHooks";
import { GetDiagnosticOrdersList_getDiagnosticOrdersList_ordersList_diagnosticOrdersStatus } from "graphql/types/GetDiagnosticOrdersList";
import { format } from "date-fns";
import { GetDiagnosticOrderDetails_getDiagnosticOrderDetails_ordersList as OrderDetails } from "graphql/types/GetDiagnosticOrderDetails";

import {
  extractPriceByGroupPlan,
  isBrowser,
  isDiagPatientUnderMinAge,
  RTPCR_COVID_ITEM_ID,
  validateGender,
} from "helpers/commonHelpers";
import { DiagnosticsWidgetData } from "components/Tests/Cards/HotSellers";
import { GetDiagnosticPackageRecommendationsBulk_getDiagnosticPackageRecommendationsBulk_packageRecommendationsBulk as DiagnosticPackageRecommendation } from "graphql/types/GetDiagnosticPackageRecommendationsBulk";
import axios from "axios";
import {
  ITEM_CONFIG_EXPIRY,
  CTA_CONFIG,
  CTA_CONFIG_EXPIRY,
  RECENT_SEARCH_ITEM_COUNT,
} from "strings/AppConfig";
import { CtaConfig } from "./CallToOrder";
import { PlanSummary } from "components/types/PlanSummary";
import { LineItems } from "components/types/LineItems";
import { PatientItems } from "components/types/PatientItems";
import { ItemLevelTATMessage } from "components/interfaces/ItemLevelTATMessage";
import { PrescriptionType } from "components/enums/PrescriptionType";
import { UploadPrescriptionType } from "components/interfaces/UploadPrescriptionType";
import { EPrescriptionFormat } from "components/interfaces/EPrescriptionFormat";
import { DiagnosticsCartContextProps } from "components/interfaces/DiagnosticsCartContextProps";
import { Clinic } from "components/interfaces/Clinic";
import { PrescriptionFormat } from "components/interfaces/PrescriptionFormat";
import { CartArea } from "components/types/CartArea";
import { ModifyOrderIdAndItemIds } from "components/types/ModifyOrderIdAndItemIds";
import { AddEditAddressType } from "components/types/AddEditAddressType";
import { ItemConfiguration } from "components/types/ItemConfiguration";
import { DiagRecentSearchItem } from "components/interfaces/DiagRecentSearchItem";
import { NonCartFlowConfig } from "components/types/NonCartFlowConfig";
import { DiagnosticsCartItem } from "components/interfaces/DiagnosticsCartItem";
import { DiagnosticAddressByPinCode } from "components/interfaces/DiagnosticAddressByPinCode";
import { ecommCircleEventObj } from "helpers/subscription_constants";
import {
  NonCartOrder,
  SuccessScreenData,
} from "./UploadPrescription/UploadPrescriptionLanding";

const defPresObject: PrescriptionFormat = {
  name: "",
  imageUrl: "",
  fileType: "",
  fileName: "",
  baseFormat: "",
};

export const DiagnosticsCartContext = createContext<DiagnosticsCartContextProps>(
  {
    selectedAddressPincode: null,
    setSelectedAddressPincode: null,
    prescriptions: null,
    setPrescriptions: null,
    prescriptionUploaded: null,
    setPrescriptionUploaded: null,
    cartCityId: null,
    setCartCityId: null,
    cartArea: null,
    setCartArea: null,
    modifyOrderId: null,
    setModifyOrderId: null,
    testsTrackingItemId: null,
    setTestsTrackingItemId: null,
    testsTrackingItemName: null,
    setTestsTrackingItemName: null,
    testsTrackingStatusArr: null,
    setTestsTrackingStatusArr: null,
    forPatientId: "",
    setPatientId: null,
    diagnosticsCartItems: [],
    setDiagnosticsCartItems: null,
    diagPatientLineItems: [],
    setDiagPatientLineItems: null,
    diagnosticTestTrackingItems: [],
    setDiagnosticTestTrackingItems: null,
    addCartItem: null,
    addPatientItems: null,
    addMultiPatientLineItems: null,
    addMultipleCartItems: null,
    removePatientFromCart: null,
    addModifyOrderId: null,
    removeModifyOrderId: null,
    removeCartItem: null,
    removeCartItemForPackage: null,
    removeUnserviseableItems: null,
    removePatientLineItem: null,
    removePatientLineItems: null,
    removePatientDuplicateLineItems: null,
    removeMultipleItemsFromCart: null,
    updateCartItem: null,
    updatePatientCartLineItems: null,
    cartTotal: 0,
    couponDiscount: 0,
    grandTotal: 0,
    coupon: null,
    setCoupon: null,
    deliveryAddressId: "",
    setDeliveryAddressId: null,
    deliveryAddresses: [],
    setDeliveryAddresses: null,
    deliveryType: null,
    clinicPinCode: "",
    setClinicPinCode: null,
    clinics: [],
    setClinics: null,
    clinicId: "",
    setClinicId: null,
    clearCartInfo: null,
    clearCartForModify: null,
    addCartInfo: null,
    setDiagnosticSlot: null,
    slotDurationInMinutes: null,
    setSlotDurationInMinutes: null,
    diagnosticSlot: null,
    ePrescriptionData: null,
    setEPrescriptionData: null,
    selectedEPrescriptionData: null,
    setSelectedEPrescriptionData: null,
    selectedPrescriptions: null,
    setSelectedPrescriptions: null,
    paidSlotCharges: null,
    setPaidSlotCharges: null,
    lastMinuteBuys: null,
    setLastMinuteBuys: null,
    lastMinuteBuysShown: null,
    setLastMinuteBuysShown: null,
    fallbackAreaName: null,
    setFallbackAreaName: null,
    itemLevelTATMessages: null,
    setItemLevelTATMessages: null,
    circleMemberShipPlan: null,
    setCircleMemberShipPlan: null,
    temporarySubscriptionId: null,
    setTemporarySubscriptionId: null,
    previouslyBookedOrder: null,
    setPreviouslyBookedOrder: null,
    autoAddressSelection: null,
    setAutoAddressSelection: null,
    diagPackageRecommendation: null,
    setDiagPackageRecommendation: null,
    diagTestRecommendation: null,
    setDiagTestRecommendation: null,
    prevDiagPackageRecommendation: null,
    setPrevDiagPackageRecommendation: null,
    previousDiagLineItems: null,
    setPreviousDiagLineItems: null,
    showUndoOption: null,
    setShowUndoOption: null,
    setPrevDiagCartItems: null,
    prevDiagCartItems: null,
    setPrevDiagPatientItems: null,
    prevDiagPatientItems: null,
    setShowCartPageUndo: null,
    showCartPageUndo: null,
    setIncludedTestsinPackage: null,
    includedTestsinPackage: null,
    sanitisePatientLineItems: null,
    showSearchBar: null,
    setShowSearchBar: null,
    openLocation: null,
    setOpenLocation: null,
    resetRecommendation: null,
    itemConfiguration: null,
    allowItemAddition: null,
    checkForCartLevelInclusionMatch: null,
    matchedInclusions: null,
    ctaConfig: null,
    showLocationPopup: null,
    setShowLocationPopup: null,
    reverifySlot: null,
    setReverifySlot: null,
    recentSearchItems: null,
    setRecentSearchItems: null,
    clearRecentSearchItems: null,
    addRecentSearchItem: null,
    prescriptionComments: null,
    setPrescriptionComments: null,
    selectedPatientIdForPrescription: null,
    setSelectedPatientIdForPrescription: null,
    nonCartFlowConfig: null,
    shouldDefault: null,
    setShouldDefault: null,
    showCircleMembershipPopup: null,
    setShowCircleMembershipPopup: null,
    diagnosticsModifyCartItems: null,
    setDiagnosticsModifyCartItems: null,
    addMultipleItemsToModifyCart: null,
    removeModifyCartItems: null,
    addModifyCartItems: null,
    getAllActiveCartItems: null,
    isItemInPrevOrder: null,
    undoModifyChanges: null,
    replacePackageRecommForPatient: null,
    triggerGARemoveItemEvent: null,
    showCartRibbon: null,
    setShowCartRibbon: null,
    nonCartOrder: null,
    setNonCartOrder: null,
    nonCartSuccessData: null,
    setNonCartSuccessData: null,
    preOpenHcPopover: null,
  }
);

export const DiagnosticsCartProvider: React.FC = (props) => {
  const [circleMemberShipPlan, setCircleMemberShipPlan] = useState<PlanSummary>(
    isBrowser() && localStorage.getItem("circleMemberShipPlan")
      ? JSON.parse(localStorage.getItem("circleMemberShipPlan"))
      : null
  );
  const [
    temporarySubscriptionId,
    setTemporarySubscriptionId,
  ] = useState<string>(null);

  const [cartArea, setCartArea] = useState<CartArea>(
    isBrowser() &&
      localStorage.getItem("testCartInfo") &&
      JSON.parse(localStorage.getItem("testCartInfo")).cartArea
      ? JSON.parse(localStorage.getItem("testCartInfo")).cartArea
      : null
  );
  const [modifyOrderId, setModifyOrderId] = useState<ModifyOrderIdAndItemIds>(
    isBrowser() && localStorage.getItem("testOrderId")
      ? JSON.parse(localStorage.getItem("testOrderId"))
      : null
  );

  const [cartCityId, setCartCityId] = useState<number>(
    isBrowser() &&
      localStorage.getItem("testCartInfo") &&
      JSON.parse(localStorage.getItem("testCartInfo")).cityID
      ? JSON.parse(localStorage.getItem("testCartInfo")).cityID
      : null
  );
  const [testsTrackingItemId, setTestsTrackingItemId] = useState<any>(null);
  const [testsTrackingItemName, setTestsTrackingItemName] = useState<string>(
    null
  );
  const [
    testsTrackingStatusArr,
    setTestsTrackingStatusArr,
  ] = useState<GetDiagnosticOrdersList_getDiagnosticOrdersList_ordersList_diagnosticOrdersStatus>(
    null
  );

  const [selectedAddressPincode, setSelectedAddressPincode] = useState<any>(
    null
  );
  const { currentPatient, allCurrentPatients } = useAuth();

  const [forPatientId, setPatientId] = useState<string>("");

  const [diagnosticsCartItems, setDiagnosticsCartItems] = useState<
    DiagnosticsCartContextProps["diagnosticsCartItems"]
  >(
    isBrowser() && localStorage.getItem("diagnosticsCartItems")
      ? JSON.parse(localStorage.getItem("diagnosticsCartItems") || "")
      : []
  );
  const [diagnosticsModifyCartItems, setDiagnosticsModifyCartItems] = useState<
    DiagnosticsCartContextProps["diagnosticsModifyCartItems"]
  >(
    isBrowser() && localStorage.getItem("diagnosticsModifyCartItems")
      ? JSON.parse(localStorage.getItem("diagnosticsModifyCartItems") || "")
      : []
  );
  const [diagPatientLineItems, setDiagPatientLineItems] = useState<
    DiagnosticsCartContextProps["diagPatientLineItems"]
  >(
    isBrowser() && localStorage.getItem("diagPatientLineItems")
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : []
  );
  const [
    diagnosticTestTrackingItems,
    setDiagnosticTestTrackingItems,
  ] = useState<DiagnosticsCartContextProps["diagnosticTestTrackingItems"]>([]);
  const [couponDiscount, setCouponDiscount] = useState<
    DiagnosticsCartContextProps["couponDiscount"]
  >(0);

  const [coupon, setCoupon] = useState<DiagnosticsCartContextProps["coupon"]>(
    null
  );
  const [deliveryAddresses, setDeliveryAddresses] = useState<
    AddEditAddressType[]
  >([]);
  const [clinicPinCode, setClinicPinCode] = useState<string>("");

  const [deliveryAddressId, _setDeliveryAddressId] = useState<
    DiagnosticsCartContextProps["deliveryAddressId"]
  >(
    isBrowser() &&
      localStorage.getItem("testCartInfo") &&
      JSON.parse(localStorage.getItem("testCartInfo")).cartAddressId
      ? JSON.parse(localStorage.getItem("testCartInfo")).cartAddressId
      : ""
  );
  const [showCartRibbon, setShowCartRibbon] = useState<boolean>(
    isBrowser() && localStorage.getItem("cartRibbonConfig")
      ? JSON.parse(localStorage.getItem("cartRibbonConfig"))?.showRibbon
      : true
  );

  const [deliveryType, setDeliveryType] = useState<
    DiagnosticsCartContextProps["deliveryType"]
  >(null);
  const [isCartUpdated, setIsCartUpdated] = useState<boolean>(false);
  const [isModifyCartUpdated, setIsModifyCartUpdated] = useState<boolean>(
    false
  );
  const [isPatientItemUpdated, setIsPatientItemUpdated] = useState<boolean>(
    false
  );
  const [paidSlotCharges, setPaidSlotCharges] = useState<number | null>(null);
  const [lastMinuteBuys, setLastMinuteBuys] = useState<DiagnosticsWidgetData[]>(
    []
  );
  const [lastMinuteBuysShown, setLastMinuteBuysShown] = useState<boolean>(
    false
  );
  const [
    previouslyBookedOrder,
    setPreviouslyBookedOrder,
  ] = useState<OrderDetails>();
  const [autoAddressSelection, setAutoAddressSelection] = useState<boolean>(
    false
  );
  const [diagPackageRecommendation, setDiagPackageRecommendation] = useState<
    DiagnosticPackageRecommendation[]
  >([]);
  const [diagTestRecommendation, setDiagTestRecommendation] = useState<
    DiagnosticsWidgetData[]
  >([]);
  const [
    prevDiagPackageRecommendation,
    setPrevDiagPackageRecommendation,
  ] = useState<DiagnosticPackageRecommendation[]>(
    isBrowser() && localStorage.getItem("prevDiagPackageRecommendation")
      ? JSON.parse(
          localStorage.getItem("prevDiagPackageRecommendation") || null
        ) || []
      : []
  );
  const [previousDiagLineItems, setPreviousDiagLineItems] = useState<{
    [key: string]: number[];
  }>(
    isBrowser() && localStorage.getItem("previousDiagLineItems")
      ? JSON.parse(localStorage.getItem("previousDiagLineItems") || null) || {}
      : {}
  );
  const [showUndoOption, _setShowUndoOption] = useState<{
    [key: string]: number;
  }>(
    isBrowser() && localStorage.getItem("showUndoOption")
      ? JSON.parse(localStorage.getItem("showUndoOption") || null) || {}
      : {}
  );
  const [prevDiagCartItems, setPrevDiagCartItems] = useState<
    DiagnosticsCartContextProps["prevDiagCartItems"]
  >(
    isBrowser() && localStorage.getItem("prevDiagCartItems")
      ? JSON.parse(localStorage.getItem("prevDiagCartItems") || null) || {}
      : {}
  );
  const [prevDiagPatientItems, setPrevDiagPatientItems] = useState<
    DiagnosticsCartContextProps["prevDiagPatientItems"]
  >(
    isBrowser() && localStorage.getItem("prevDiagPatientItems")
      ? JSON.parse(localStorage.getItem("prevDiagPatientItems") || null) || {}
      : {}
  );
  const [showCartPageUndo, setShowCartPageUndo] = useState<{
    [id: number]: boolean;
  }>(
    isBrowser() && localStorage.getItem("showCartPageUndo")
      ? JSON.parse(localStorage.getItem("showCartPageUndo") || null) || {}
      : {}
  );
  const [includedTestsinPackage, setIncludedTestsinPackage] = useState<
    DiagnosticsCartContextProps["includedTestsinPackage"]
  >(
    isBrowser() && localStorage.getItem("includedTestsinPackage")
      ? JSON.parse(localStorage.getItem("includedTestsinPackage") || null) || {}
      : {}
  );
  const [itemConfiguration, setItemConfiguration] = useState<ItemConfiguration>(
    {}
  );
  const [showSearchBar, setShowSearchBar] = useState<boolean>(
    isBrowser() && localStorage.getItem("showSearchBar") ? true : false
  );
  const [recentSearchItems, setRecentSearchItems] = useState<
    DiagRecentSearchItem[]
  >(
    isBrowser() && localStorage.getItem("diagRecentSearchItems")
      ? (JSON.parse(
          localStorage.getItem("diagRecentSearchItems")
        ) as DiagRecentSearchItem[])
      : []
  );
  const [
    showCircleMembershipPopup,
    setShowCircleMembershipPopup,
  ] = useState<boolean>(false);
  const [matchedInclusions, setMatchedInclusions] = useState<{
    [key: string]: string[];
  }>({});

  const [preOpenHcPopover, setPreOpenHcPopover] = useState<boolean>(false);

  const setShowUndoOption = (value: { [key: string]: number }) => {
    _setShowUndoOption(value);
    localStorage.setItem("showUndoOption", JSON.stringify(value));
  };

  const addRecentSearchItem: DiagnosticsCartContextProps["addRecentSearchItem"] = (
    recentItem
  ) => {
    const recentSearchItemsFromStorage: DiagRecentSearchItem[] =
      JSON.parse(localStorage?.getItem("diagRecentSearchItems")) || [];

    const isItemPresent = !!recentSearchItemsFromStorage?.find(
      (item) => item?.diagnostic_item_id === recentItem?.diagnostic_item_id
    );

    const filteredRecentItems = recentSearchItemsFromStorage
      .filter(
        (item) => item?.diagnostic_item_id !== recentItem?.diagnostic_item_id
      )
      .slice(0, RECENT_SEARCH_ITEM_COUNT);

    if (isItemPresent) {
      recentItem.visible = true;
    }

    const newRecentSearchItems = [recentItem, ...filteredRecentItems];
    setRecentSearchItems(newRecentSearchItems);
    localStorage.setItem(
      "diagRecentSearchItems",
      JSON.stringify(newRecentSearchItems)
    );
  };

  useEffect(() => {
    if (isBrowser() && !showCartRibbon) {
      const cartRibbonConfig = {
        showRibbon: showCartRibbon,
      };
      localStorage.setItem(
        "cartRibbonConfig",
        JSON.stringify(cartRibbonConfig)
      );
    }
  }, [showCartRibbon]);

  const clearRecentSearchItems = () => {
    setRecentSearchItems([]);
    localStorage.removeItem("diagRecentSearchItems");
  };

  const allowItemAddition: DiagnosticsCartContextProps["allowItemAddition"] = (
    patientId,
    itemId,
    sourceItemIds
  ) => {
    const restrictedItems = itemConfiguration[itemId?.toString()];
    // If restricted items, allow addition
    if (!restrictedItems?.length) return true;

    // This check is for modify mode where we get item ids
    if (sourceItemIds?.length) {
      return restrictedItems.every(
        (item) => !sourceItemIds?.includes(Number(item))
      );
    }

    const currentPatientLineItems = diagPatientLineItems?.find(
      (patientLineItems) => patientLineItems?.patientID === patientId
    )?.lineItems;

    // If no line items, allow addition
    if (!currentPatientLineItems?.length) return true;

    // check if restricted items are not in cart
    const allowAddition = restrictedItems?.every(
      (item) =>
        !currentPatientLineItems.some(
          (lineItem) => lineItem?.itemId === Number(item)
        )
    );
    return allowAddition;
  };

  const isConflictingItemMapped = (
    addedItem: DiagnosticsCartItem,
    patientId: string,
    cartItems: DiagnosticsCartItem[]
  ): boolean => {
    const conflictData = getInclusionConflicts(cartItems);
    let isConflict = false;
    if (conflictData) {
      Object.entries(conflictData)?.forEach(([itemId, includedItems]) => {
        // If item to be added is key, check if any inclusions are mapped to patient
        if (itemId === addedItem?.itemId) {
          isConflict =
            isConflict ||
            includedItems?.some((inc) => isItemMappedToPatient(patientId, inc));
        } else if (includedItems?.includes(addedItem?.itemId)) {
          // If item to be added is an inclusion, check if corresponding key is mapped to patient
          isConflict = isConflict || isItemMappedToPatient(patientId, itemId);
        }
        // If conflict found, return immediately
        if (isConflict) return true;
      });
    }
    return isConflict;
  };

  const addMultiPatientItem = (
    item: DiagnosticsCartItem,
    newCartItems: DiagnosticsCartItem[]
  ) => {
    const diagPatientLineItems = localStorage.getItem("diagPatientLineItems")
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    diagPatientLineItems?.forEach((patientLineItems) => {
      const shouldAllowAddition =
        validateGender(patientLineItems?.gender, item?.gender) &&
        allowItemAddition(patientLineItems?.patientID, item?.itemId) &&
        !isConflictingItemMapped(
          item,
          patientLineItems?.patientID,
          newCartItems
        );

      if (shouldAllowAddition) {
        const activePatient = allCurrentPatients?.find(
          (patients) => patients?.id === patientLineItems?.patientID
        );
        if (isDiagPatientUnderMinAge(activePatient)) {
          if (item?.itemId === RTPCR_COVID_ITEM_ID) {
            const lineItems: LineItems = extractPriceByGroupPlan(item);
            patientLineItems.totalPrice =
              patientLineItems.totalPrice + lineItems?.price;
            patientLineItems.lineItems.push(lineItems);
          }
        } else {
          const lineItems: LineItems = extractPriceByGroupPlan(item);
          patientLineItems.totalPrice =
            patientLineItems.totalPrice + lineItems?.price;
          patientLineItems.lineItems.push(lineItems);
        }
      }
    });
    setDiagPatientLineItems(diagPatientLineItems);
    setIsPatientItemUpdated(true);
    setDiagnosticsCartItems(newCartItems);
  };

  const resetRecommendation = () => {
    setShowUndoOption({});
    setDiagPackageRecommendation(null);
    setPrevDiagPackageRecommendation(null);
    setPreviousDiagLineItems({});
    setShowCartPageUndo(null);
    setPrevDiagCartItems(null);
    setPrevDiagPatientItems(null);
    setIncludedTestsinPackage(null);
    localStorage.removeItem("includedTestsinPackage");
    localStorage.removeItem("showCartPageUndo");
    localStorage.removeItem("prevDiagCartItems");
    localStorage.removeItem("prevDiagPatientItems");
    localStorage.removeItem("showUndoOption");
    localStorage.removeItem("prevDiagPackageRecommendation");
    localStorage.removeItem("previousDiagLineItems");
  };

  const getInclusionConflicts = (cartItems: DiagnosticsCartItem[]) => {
    const inclusionConfig: { [key: string]: string[] } = {};
    cartItems?.forEach((cartItem) => {
      // Inclusions will match only if cartItem is package, hence check for package
      if (cartItem?.inclusions?.length > 1) {
        const remDiagItems = cartItems.filter(
          (diagItem) => diagItem?.id !== cartItem?.itemId
        );
        const matchedItems = remDiagItems
          .filter((remItem) => {
            // If remItem has inclusions, go for inclusion match
            if (remItem?.inclusions?.length) {
              return (
                cartItem?.inclusions?.length > remItem?.inclusions?.length &&
                remItem?.inclusions?.every((inclusion) =>
                  cartItem?.inclusions?.includes(inclusion)
                )
              );
            } else {
              // check if remItem is an inclusion of cartItem
              return cartItem?.inclusions?.includes(Number(remItem?.itemId));
            }
          })
          ?.map((item) => item?.itemId);
        inclusionConfig[cartItem?.itemId] = matchedItems || [];
      } else {
        inclusionConfig[cartItem?.itemId] = [];
      }
    });
    return inclusionConfig;
  };

  useEffect(() => {
    if (isBrowser()) {
      const localStorageOpenHCPopoverExist = localStorage.getItem(
        "preOpenHCPopover"
      );
      if (localStorageOpenHCPopoverExist) {
        setPreOpenHcPopover(JSON.parse(localStorageOpenHCPopoverExist));
      } else {
        setPreOpenHcPopover(true);
        localStorage.setItem("preOpenHCPopover", "true");
      }
    }
  }, []);

  useEffect(() => {
    if (diagnosticsCartItems?.length > 0) {
      const inclusionConfig = getInclusionConflicts(diagnosticsCartItems);
      setMatchedInclusions(inclusionConfig);
    }
  }, [diagnosticsCartItems]);

  const isItemMappedToPatient = (
    patientID: string,
    itemId: string
  ): boolean => {
    const patientLineItem = diagPatientLineItems?.find(
      (patient: PatientItems) => patient?.patientID === patientID
    );
    return !!patientLineItem?.lineItems?.some(
      (lineItem: LineItems) => lineItem?.itemId === parseInt(itemId)
    );
  };

  /**
   * Checks for inclusion match among cart items
   * @param cartItem cart item to be checked
   * @param patientId patient id for which check has to be done
   * @param excludedItemId item id to be excluded while check
   * @returns true, if the given item or it's inclusions are completely included in any other cart item
   */
  const checkForCartLevelInclusionMatch = (
    cartItem: DiagnosticsCartItem,
    patientId?: string,
    excludedItemId?: string
  ) => {
    if (matchedInclusions) {
      const includedIn = [];
      Object.entries(matchedInclusions)?.forEach(([itemId, inclusions]) => {
        if (inclusions?.includes(cartItem?.itemId)) {
          if (excludedItemId) {
            itemId !== excludedItemId && includedIn.push(itemId);
          } else {
            includedIn.push(itemId);
          }
        }
      });
      if (patientId) {
        return (
          !!includedIn?.length &&
          !!includedIn.some((itemId) =>
            isItemMappedToPatient(patientId, itemId)
          )
        );
      } else {
        return !!includedIn?.length;
      }
    }
    return false;
  };

  const addCartItem: DiagnosticsCartContextProps["addCartItem"] = (
    itemToAdd,
    keepUndoOption,
    patientId
  ) => {
    const diagnosticsCartItems: DiagnosticsCartItem[] = localStorage.getItem(
      "diagnosticsCartItems"
    )
      ? JSON.parse(localStorage.getItem("diagnosticsCartItems") || "")
      : [];
    const itemInCart = diagnosticsCartItems.find(
      (item: DiagnosticsCartItem) => item.id === itemToAdd.id
    );

    const newCartItems = !itemInCart
      ? [itemToAdd, ...diagnosticsCartItems]
      : diagnosticsCartItems?.map((cartItem) =>
          cartItem?.itemId === itemInCart?.itemId ? itemToAdd : cartItem
        );
    if (patientId) {
      const activePatient = allCurrentPatients?.find(
        (patients) => patients?.id === patientId
      );
      const lineItems: LineItems = extractPriceByGroupPlan(
        itemToAdd,
        !!circleMemberShipPlan
      );
      addPatientItems(patientId, lineItems, activePatient?.gender);
      setDiagnosticsCartItems(newCartItems);
      setIsCartUpdated(true);
    } else {
      addMultiPatientItem(itemToAdd, newCartItems);
    }
    setIsCartUpdated(true);
    if (!keepUndoOption) {
      resetRecommendation();
    }
  };

  const triggerGARemoveItemEvent = (
    itemId: number,
    diagnosticAddress: DiagnosticAddressByPinCode,
    isUserCare: boolean
  ) => {
    const cartItem =
      diagnosticsCartItems?.find(
        (cartItem) => Number(cartItem?.itemId) === Number(itemId)
      ) ||
      diagnosticsModifyCartItems?.find(
        (cartItem) => Number(cartItem?.itemId) === Number(itemId)
      );
    const { circlePrice, allPrice, circleMrp, allMrp } =
      cartItem?.diagnosticPricing || {};
    const totalDiscount =
      isUserCare && circlePrice > 0
        ? circleMrp - circlePrice
        : allMrp - allPrice;
    const displayPrice = isUserCare && circlePrice > 0 ? circlePrice : allPrice;
    const eventPayload = {
      event: "remove_from_cart",
      lob: "Diagnostic",
      section: "lab_tests",
      uhid: currentPatient?.uhid || "NA",
      chronic_diagnos: "NA",
      ecommerce: {
        currency: "INR",
        value: displayPrice,
        items: [
          {
            ...ecommCircleEventObj,
            item_id: cartItem?.itemId,
            item_name: cartItem?.name,
            discount: totalDiscount,
            index: 0,
            item_category3: diagnosticAddress?.cityName || "NA",
            item_category4: diagnosticAddress?.cityName
              ? `${diagnosticAddress?.cityName?.toLowerCase()}_${diagnosticAddress?.stateName?.toLowerCase()}`
              : "NA",
            location_id: diagnosticAddress?.cityName
              ? `${diagnosticAddress?.latitude}_${diagnosticAddress?.longitude}`
              : "NA",
            price: displayPrice,
            quantity: 1,
          },
        ],
      },
    };
    window.dataLayer.push(eventPayload);
  };

  const addPatientItems: DiagnosticsCartContextProps["addPatientItems"] = (
    patientID: string,
    lineItems: LineItems,
    gender: string
  ) => {
    const patientLineItems: PatientItems[] =
      isBrowser() && localStorage.getItem("diagPatientLineItems")
        ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
        : [];
    const findPatientIndex = patientLineItems?.findIndex(
      (patient: PatientItems) => patient.patientID === patientID
    );
    if (findPatientIndex !== -1) {
      if (allowItemAddition(patientID, lineItems?.itemId?.toString())) {
        patientLineItems[findPatientIndex].totalPrice =
          patientLineItems[findPatientIndex].totalPrice + lineItems?.price;
        patientLineItems[findPatientIndex].lineItems.push(lineItems);
      } else {
        // if blocked item is getting added, remove all the restricted items from cart
        const restrictedItems = itemConfiguration[lineItems.itemId?.toString()];
        const newLineItems = patientLineItems[
          findPatientIndex
        ].lineItems?.filter(
          (lineItem) => !restrictedItems?.includes(lineItem?.itemId?.toString())
        );
        newLineItems?.push(lineItems);
        const totalPrice = newLineItems?.reduce(
          (total, item) => total + item?.price,
          0
        );
        patientLineItems[findPatientIndex].totalPrice = totalPrice;
        patientLineItems[findPatientIndex].lineItems = newLineItems;
      }
      const items = JSON.stringify(patientLineItems);
      localStorage.setItem("diagPatientLineItems", items);
      setDiagPatientLineItems(patientLineItems);
    } else {
      const patientLineItemObj: PatientItems = {
        totalPrice: lineItems?.price,
        lineItems: [lineItems],
        patientID: patientID,
        gender,
      };
      const newPatientLineItem = [patientLineItemObj, ...patientLineItems];
      const items = JSON.stringify(newPatientLineItem);
      localStorage.setItem("diagPatientLineItems", items);
      setDiagPatientLineItems(newPatientLineItem);
    }
  };

  const addMultiPatientLineItems: DiagnosticsCartContextProps["addMultiPatientLineItems"] = (
    patientID: string,
    lineItems: LineItems[],
    gender,
    keepUndoOption
  ) => {
    const patientLineItems = localStorage.getItem("diagPatientLineItems")
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    const patientIndex = patientLineItems?.findIndex(
      (patientLineItem) => patientLineItem?.patientID === patientID
    );
    const totalPrice = lineItems?.reduce((count, row) => count + row?.price, 0);
    const patientLineItemObj: PatientItems = {
      totalPrice: totalPrice,
      lineItems: lineItems,
      patientID: patientID,
      gender: gender,
    };
    if (patientIndex !== -1) {
      patientLineItems[patientIndex] = patientLineItemObj;
      setDiagPatientLineItems(patientLineItems);
    } else {
      const newPatientLineItem = [patientLineItemObj, ...patientLineItems];
      setDiagPatientLineItems(newPatientLineItem);
    }
    setIsPatientItemUpdated(true);
    if (!keepUndoOption) {
      resetRecommendation();
    }
  };

  const removePatientFromCart: DiagnosticsCartContextProps["removePatientFromCart"] = (
    patienID: string,
    keepUndoOption
  ) => {
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    const findPatientIndex = patientLineItems?.findIndex(
      (patientCart: PatientItems) => patientCart.patientID === patienID
    );
    if (findPatientIndex !== -1) {
      patientLineItems?.splice(findPatientIndex, 1);
    }

    const newPatientLineItem: PatientItems[] = patientLineItems?.filter(
      (patient: PatientItems) => patient?.lineItems?.length > 0
    );
    setDiagPatientLineItems(newPatientLineItem);
    if (Array.isArray(patientLineItems) && patientLineItems.length === 0) {
      localStorage.removeItem("diagPatientLineItems");
    }
    setIsPatientItemUpdated(true);
    if (!keepUndoOption) {
      resetRecommendation();
    }
  };

  const removePatientDuplicateLineItems: DiagnosticsCartContextProps["removePatientDuplicateLineItems"] = (
    itemIds: number[],
    patientID: string
  ) => {
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    itemIds?.forEach((id: number) => {
      const findPatientIndex = patientLineItems?.findIndex(
        (patient: PatientItems) => patient?.patientID === patientID
      );
      if (findPatientIndex !== -1) {
        const lineItemsIndex = patientLineItems[
          findPatientIndex
        ]?.lineItems?.findIndex(
          (lineItem: LineItems) => lineItem?.itemId === id
        );
        if (lineItemsIndex !== -1) {
          patientLineItems[findPatientIndex].totalPrice =
            patientLineItems[findPatientIndex]?.totalPrice -
            patientLineItems[findPatientIndex].lineItems[lineItemsIndex]?.price;
          patientLineItems[findPatientIndex]?.lineItems?.splice(
            lineItemsIndex,
            1
          );
        }
      }
      const newPatientLineItems: PatientItems[] = patientLineItems?.filter(
        (patient: PatientItems) => patient?.lineItems?.length > 0
      );

      const items = JSON.stringify(newPatientLineItems);
      localStorage.setItem("diagPatientLineItems", items);
      setDiagPatientLineItems(newPatientLineItems);
      checkItemRemovedFromAllPatients(id);
      if (
        Array.isArray(newPatientLineItems) &&
        newPatientLineItems.length === 0
      ) {
        localStorage.removeItem("diagPatientLineItems");
      }
    });
  };

  /**
   * Removes cart items that are not added to any patient line item
   */
  const removeUnassignedCartItems = () => {
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];

    const allAssignedItemIds = patientLineItems?.reduce(
      (itemIds, patientLineItem) => {
        patientLineItem?.lineItems?.forEach(
          (item) =>
            !itemIds?.includes(item?.itemId) && itemIds.push(item?.itemId)
        );
        return itemIds;
      },
      [] as number[]
    );

    const diagnosticsCartItems: DiagnosticsCartItem[] = localStorage?.getItem(
      "diagnosticsCartItems"
    )
      ? JSON.parse(localStorage.getItem("diagnosticsCartItems") || "")
      : [];
    const filteredCartItems =
      diagnosticsCartItems?.filter((cartItem) =>
        allAssignedItemIds?.includes(Number(cartItem?.itemId))
      ) || [];

    localStorage.setItem(
      "diagnosticsCartItems",
      JSON.stringify(filteredCartItems)
    );
    setDiagnosticsCartItems(filteredCartItems);
    setIsCartUpdated(true);
  };

  const sanitisePatientLineItems = () => {
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    const newPatientLineItems = patientLineItems.reduce(
      (currentLineItems, lineItem) => {
        if (
          !currentLineItems.find(
            (item) => item.patientID === lineItem?.patientID
          )
        ) {
          currentLineItems.push(lineItem);
        }
        return currentLineItems;
      },
      [] as PatientItems[]
    );
    const items = JSON.stringify(newPatientLineItems);
    localStorage.setItem("diagPatientLineItems", items);
    setDiagPatientLineItems(newPatientLineItems);
    removeUnassignedCartItems();
  };

  const removeUnserviseableItems: DiagnosticsCartContextProps["removeUnserviseableItems"] = (
    itemIds: string[]
  ) => {
    itemIds?.forEach((itemId: string) => {
      const cartPatient: PatientItems[] = [];
      const patientLineItems: PatientItems[] = localStorage.getItem(
        "diagPatientLineItems"
      )
        ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
        : [];
      patientLineItems?.forEach((patient: PatientItems) => {
        const lineItemsIndex = patient?.lineItems?.findIndex(
          (lineItem: LineItems) => lineItem?.itemId === parseInt(itemId, 10)
        );
        if (lineItemsIndex !== -1) {
          patient.totalPrice =
            patient?.totalPrice - patient.lineItems[lineItemsIndex]?.price;
          patient?.lineItems?.splice(lineItemsIndex, 1);
        }
        if (patient?.lineItems?.length > 0) {
          cartPatient.push(patient);
        }
      });
      const items = JSON.stringify(cartPatient);
      localStorage.setItem("diagPatientLineItems", items);
      setDiagPatientLineItems(cartPatient);
      setIsPatientItemUpdated(true);
      if (Array.isArray(cartPatient) && cartPatient.length === 0) {
        localStorage.removeItem("diagPatientLineItems");
      }
    });
  };

  const checkItemRemovedFromAllPatients = (cartItemId: number) => {
    let isItemExistInPatientCart = true;
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    patientLineItems?.forEach((patient: PatientItems) => {
      const findItem = patient?.lineItems?.some(
        (itemObj: LineItems) => itemObj?.itemId === cartItemId
      );
      if (findItem) {
        isItemExistInPatientCart = false;
      }
    });
    if (isItemExistInPatientCart) {
      removeCartItem(cartItemId.toString());
    }
  };

  const removePatientLineItem: DiagnosticsCartContextProps["removePatientLineItem"] = (
    patientID: string,
    itemId: string,
    lineItems: LineItems
  ) => {
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    const newPatientLineItems: PatientItems[] = patientLineItems
      ?.map((patient: PatientItems) => {
        return {
          totalPrice:
            patient?.patientID === patientID
              ? patient?.totalPrice - lineItems?.price
              : patient?.totalPrice,
          lineItems:
            patient?.patientID === patientID
              ? patient?.lineItems?.filter(
                  (items: LineItems) => items?.itemId !== parseInt(itemId, 10)
                )
              : patient?.lineItems,
          patientID: patient?.patientID,
          gender: patient?.gender,
        };
      })
      ?.filter((patient: PatientItems) => patient?.lineItems?.length > 0);
    setDiagPatientLineItems(newPatientLineItems);
    if (
      Array.isArray(newPatientLineItems) &&
      newPatientLineItems.length === 0
    ) {
      localStorage.removeItem("diagPatientLineItems");
    }
    setIsPatientItemUpdated(true);
  };

  const removePatientLineItems: DiagnosticsCartContextProps["removePatientLineItems"] = (
    patientUhid: string,
    lineItems: LineItems[]
  ) => {
    const totalPrice = lineItems?.reduce((total, cur) => total + cur?.price, 0);
    const removedItems = lineItems?.map((item) => item?.itemId);
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    const newPatientLineItems: PatientItems[] = patientLineItems
      ?.map((patient: PatientItems) => {
        return {
          totalPrice:
            patient?.patientID === patientUhid
              ? patient?.totalPrice - totalPrice
              : patient?.totalPrice,
          lineItems:
            patient?.patientID === patientUhid
              ? patient?.lineItems?.filter(
                  (items: LineItems) => !removedItems?.includes(items?.itemId)
                )
              : patient?.lineItems,

          patientID: patient?.patientID,
          gender: patient?.gender,
        };
      })
      ?.filter((patient: PatientItems) => patient?.lineItems?.length > 0);

    if (Array.isArray(newPatientLineItems) && newPatientLineItems.length > 0) {
      const items = JSON.stringify(newPatientLineItems);
      localStorage.setItem("diagPatientLineItems", items);
    }
    setDiagPatientLineItems(newPatientLineItems);
    if (Array.isArray(patientLineItems) && patientLineItems.length === 0) {
      localStorage.removeItem("diagPatientLineItems");
    }
    setIsPatientItemUpdated(true);
  };

  const replacePackageRecommForPatient = (
    patientId: string,
    lineItemsToRemove: LineItems[],
    packageRecommToAdd: DiagnosticsCartItem
  ) => {
    const totalPrice = lineItemsToRemove?.reduce(
      (total, cur) => total + cur?.price,
      0
    );
    const removedItems = lineItemsToRemove?.map((item) => item?.itemId);
    const patientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];

    // Remove patient line items for given patient
    const updatedPatientLineItems: PatientItems[] = patientLineItems
      ?.map((patient: PatientItems) => {
        return {
          totalPrice:
            patient?.patientID === patientId
              ? patient?.totalPrice - totalPrice
              : patient?.totalPrice,
          lineItems:
            patient?.patientID === patientId
              ? patient?.lineItems?.filter(
                  (items: LineItems) => !removedItems?.includes(items?.itemId)
                )
              : patient?.lineItems,

          patientID: patient?.patientID,
          gender: patient?.gender,
        };
      })
      ?.filter((patient: PatientItems) => patient?.lineItems?.length > 0);

    if (
      Array.isArray(updatedPatientLineItems) &&
      updatedPatientLineItems.length > 0
    ) {
      const items = JSON.stringify(updatedPatientLineItems);
      localStorage.setItem("diagPatientLineItems", items);
    } else {
      localStorage.removeItem("diagPatientLineItems");
    }

    // Add package to diagnosticsCartItem
    const diagnosticsCartItems: DiagnosticsCartItem[] = localStorage.getItem(
      "diagnosticsCartItems"
    )
      ? JSON.parse(localStorage.getItem("diagnosticsCartItems") || "")
      : [];
    const itemInCart = diagnosticsCartItems.find(
      (item: DiagnosticsCartItem) => item.id === packageRecommToAdd.id
    );

    const newCartItems = !itemInCart
      ? [packageRecommToAdd, ...diagnosticsCartItems]
      : diagnosticsCartItems?.map((cartItem) =>
          cartItem?.itemId === itemInCart?.itemId
            ? packageRecommToAdd
            : cartItem
        );

    if (patientId) {
      const activePatient = allCurrentPatients?.find(
        (patients) => patients?.id === patientId
      );
      const lineItems: LineItems = extractPriceByGroupPlan(
        packageRecommToAdd,
        !!circleMemberShipPlan
      );
      addPatientItems(patientId, lineItems, activePatient?.gender);
      setDiagnosticsCartItems(newCartItems);
      setIsCartUpdated(true);
    }
  };

  const addModifyOrderId: DiagnosticsCartContextProps["addModifyOrderId"] = (
    items: ModifyOrderIdAndItemIds
  ) => {
    localStorage.setItem("testOrderId", JSON.stringify(items));
    setModifyOrderId(items);
  };

  const removeModifyOrderId: DiagnosticsCartContextProps["removeModifyOrderId"] = () => {
    localStorage.removeItem("testOrderId");
    localStorage.removeItem("diagnosticsModifyCartItems");
    localStorage.removeItem("diagnosticsCartItems");
    setDiagnosticsModifyCartItems([]);
    setDiagnosticsCartItems([]);
    setModifyOrderId(null);
  };

  const addMultipleCartItems: DiagnosticsCartContextProps["addMultipleCartItems"] = (
    itemsToAdd
  ) => {
    const diagnosticsCartItems = localStorage.getItem("diagnosticsCartItems")
      ? JSON.parse(localStorage.getItem("diagnosticsCartItems") || "")
      : [];
    // If tried to add same items(by id) which already exists in the cart, it will update with new values.
    const existingFilteredCartItems = diagnosticsCartItems.filter(
      (item) => !itemsToAdd.find((val) => val.id == item.id)
    );
    const newCartItems = [
      ...existingFilteredCartItems,
      ...itemsToAdd.filter(
        (v, i, a) => a.findIndex((t) => t.id === v.id) === i
      ),
    ];
    setDiagnosticsCartItems(newCartItems);
    setIsCartUpdated(true);
    resetRecommendation();
  };

  const removeMultiPatientItem = (itemId: string) => {
    const diagPatientLineItems: PatientItems[] = localStorage.getItem(
      "diagPatientLineItems"
    )
      ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
      : [];
    diagPatientLineItems?.forEach((patientLineItems) => {
      const findLineItemIndex = patientLineItems?.lineItems?.findIndex(
        (items: LineItems) => items?.itemId === parseInt(itemId, 10)
      );
      if (findLineItemIndex !== -1) {
        patientLineItems.totalPrice =
          patientLineItems.totalPrice -
          patientLineItems.lineItems[findLineItemIndex]?.price;
        patientLineItems?.lineItems?.splice(findLineItemIndex, 1);
      }
    });
    setDiagPatientLineItems(diagPatientLineItems);
    setIsPatientItemUpdated(true);
  };

  const removeMultiplePatientItems = (itemIds: string[]) => {
    diagPatientLineItems?.forEach((patientLineItems) => {
      const newLineItems = patientLineItems?.lineItems?.filter(
        (lineItem) => !itemIds?.includes(lineItem?.itemId?.toString())
      );
      patientLineItems.lineItems = newLineItems;
    });
    setDiagPatientLineItems(diagPatientLineItems);
    setIsPatientItemUpdated(true);
  };

  const removeCartItem: DiagnosticsCartContextProps["removeCartItem"] = (
    itemId
  ) => {
    const newCartItems = diagnosticsCartItems.filter(
      (item) => parseInt(item.itemId, 10) !== parseInt(itemId, 10)
    );
    setDiagnosticsCartItems(newCartItems);
    if (Array.isArray(newCartItems) && newCartItems.length === 0) {
      localStorage.removeItem("testCartInfo");
      localStorage.removeItem("circleMemberShipPlan");
    }
    removeMultiPatientItem(itemId);
    setIsCartUpdated(true);
    resetRecommendation();
  };

  const removeCartItemForPackage: DiagnosticsCartContextProps["removeCartItemForPackage"] = (
    itemIds
  ) => {
    const newCartItems = diagnosticsCartItems?.filter((item) => {
      return !itemIds?.includes(item?.itemId);
    });
    setDiagnosticsCartItems(newCartItems);
    setIsCartUpdated(true);
    resetRecommendation();
  };

  const removeMultipleItemsFromCart: DiagnosticsCartContextProps["removeMultipleItemsFromCart"] = (
    itemIdsArr
  ) => {
    if (diagnosticsCartItems.length > 0) {
      const newCartItems = diagnosticsCartItems.filter(
        (item) => !itemIdsArr.includes(item.itemId)
      );
      setDiagnosticsCartItems(newCartItems);
      removeMultiplePatientItems(itemIdsArr);
      if (Array.isArray(newCartItems) && newCartItems.length === 0) {
        localStorage.removeItem("testCartInfo");
      }
      setIsCartUpdated(true);
    }
  };

  const updateCartItem: DiagnosticsCartContextProps["updateCartItem"] = (
    itemUpdates
  ) => {
    const foundIndex = diagnosticsCartItems.findIndex(
      (item) => item.id == itemUpdates.id
    );
    if (foundIndex !== -1) {
      diagnosticsCartItems[foundIndex] = {
        ...diagnosticsCartItems[foundIndex],
        ...itemUpdates,
      };
      setDiagnosticsCartItems([...diagnosticsCartItems]);
      setIsCartUpdated(true);
    }
  };

  const updatePatientCartLineItems: DiagnosticsCartContextProps["updatePatientCartLineItems"] = useCallback(
    (lineItems: LineItems, itemId: number) => {
      const patientLineItems = localStorage.getItem("diagPatientLineItems")
        ? JSON.parse(localStorage.getItem("diagPatientLineItems") || "")
        : [];
      const newPatientLineItem = patientLineItems?.map(
        (patientItems: PatientItems) => {
          const findLineItemsIndex = patientItems?.lineItems?.findIndex(
            (lineItems: LineItems) => lineItems?.itemId === itemId
          );
          if (findLineItemsIndex !== -1) {
            patientItems.lineItems[findLineItemsIndex] = lineItems;
            const totalPrice = patientItems?.lineItems?.reduce(
              (count, row) => count + row?.price,
              0
            );
            const patientLineItemObj: PatientItems = {
              totalPrice: totalPrice,
              lineItems: patientItems?.lineItems,
              patientID: patientItems?.patientID,
            };
            return patientLineItemObj;
          } else {
            return patientItems;
          }
        }
      );
      const items = JSON.stringify(newPatientLineItem);
      localStorage.setItem("diagPatientLineItems", items);
      setDiagPatientLineItems(newPatientLineItem);
    },
    []
  );

  const cartTotal: DiagnosticsCartContextProps["cartTotal"] = parseFloat(
    diagnosticsCartItems
      .reduce(
        (currTotal, currItem) =>
          currTotal + (currItem.specialPrice || currItem.price),
        0
      )
      .toFixed(2)
  );

  const grandTotal = parseFloat((cartTotal + 0 - couponDiscount).toFixed(2));

  const setDeliveryAddressId = (
    id: DiagnosticsCartContextProps["deliveryAddressId"]
  ) => {
    setDeliveryType(MEDICINE_DELIVERY_TYPE.HOME_DELIVERY);
    _setDeliveryAddressId(id);
  };

  const clearCartInfo = () => {
    localStorage.removeItem(`diagnosticsCartItems`);
    localStorage.removeItem("testOrderId");
    localStorage.removeItem("diagnosticsModifyCartItems");
    localStorage.removeItem("diagPatientLineItems");
    localStorage.removeItem("circleMemberShipPlan");
    setModifyOrderId(null);
    setDiagnosticsModifyCartItems([]);
    setDiagnosticsCartItems([]);
    setDiagPatientLineItems([]);
    setDeliveryAddressId("");
    setClinicPinCode("");
    setClinicId("");
    setCoupon(null);
    setIsCartUpdated(true);
    setIsModifyCartUpdated(true);
    setIsPatientItemUpdated(true);
    setCircleMemberShipPlan(null);
    localStorage.removeItem("testsPrescriptions");
    localStorage.removeItem("testsEPrescriptions");
    localStorage.removeItem("ePrescriptionData");
    localStorage.removeItem("testCartInfo");
    localStorage.removeItem("codConversionData");
    resetRecommendation();
  };

  const clearCartForModify = () => {
    localStorage.removeItem(`diagnosticsCartItems`);
    localStorage.removeItem("testOrderId");
    localStorage.removeItem("diagnosticsModifyCartItems");
    setModifyOrderId(null);
    setDiagnosticsModifyCartItems([]);
    setDiagnosticsCartItems([]);
  };

  const addMultipleItemsToModifyCart = (cartItems: DiagnosticsCartItem[]) => {
    if (!cartItems?.length) return;

    if (!diagnosticsModifyCartItems?.length) {
      setDiagnosticsModifyCartItems([...cartItems]);
    } else {
      const itemsToBeAdded = cartItems?.map((item) => {
        const alreadyAddedItem = diagnosticsModifyCartItems?.find(
          (existingItem) => existingItem?.itemId === item?.itemId
        );

        return alreadyAddedItem
          ? { ...item, isRemoved: alreadyAddedItem.isRemoved }
          : { ...item };
      });
      setDiagnosticsModifyCartItems(itemsToBeAdded);
    }
    setIsModifyCartUpdated(true);
  };

  const getAllActiveCartItems = () => {
    const activeCartItems: DiagnosticsCartItem[] = [];
    diagnosticsCartItems
      ?.filter((itemId) => itemId !== null)
      ?.forEach((cartItem) => activeCartItems.push(cartItem));
    diagnosticsModifyCartItems
      ?.filter((cartItem) => !cartItem?.isRemoved)
      ?.forEach((cartItem) => activeCartItems.push(cartItem));
    return activeCartItems;
  };

  const isItemInPrevOrder = (itemId: number | string) =>
    itemId ? modifyOrderId?.itemIds?.includes(Number(itemId)) : false;

  const removeModifyCartItems = (itemIds: string[]) => {
    setDiagnosticsModifyCartItems((prevCartItems) => {
      const updatedModifyCartItems = prevCartItems?.map((item) =>
        itemIds?.includes(item?.itemId)
          ? { ...item, isRemoved: true }
          : { ...item }
      );

      return updatedModifyCartItems;
    });
    setIsModifyCartUpdated(true);
  };

  const addModifyCartItems = (itemIds: string[]) => {
    setDiagnosticsModifyCartItems((prevCartItems) => {
      const updatedModifyCartItems = prevCartItems?.map((item) =>
        itemIds?.includes(item?.itemId)
          ? { ...item, isRemoved: false }
          : { ...item }
      );

      return updatedModifyCartItems;
    });
    setIsModifyCartUpdated(true);
  };

  const undoModifyChanges = () => {
    setDiagnosticsModifyCartItems((prevCartItems) => {
      const updatedModifyCartItems: DiagnosticsCartItem[] = prevCartItems?.map(
        (item) => ({
          ...item,
          isRemoved: false,
        })
      );

      return updatedModifyCartItems;
    });
    setDiagnosticsCartItems([]);
    setIsModifyCartUpdated(true);
    setIsCartUpdated(true);
  };

  const addCartInfo = (infoObj: any) => {
    const getCartInfo = JSON.parse(localStorage.getItem("testCartInfo"));
    if (getCartInfo) {
      const storeCartInfo = { ...getCartInfo, ...infoObj };
      localStorage.setItem("testCartInfo", JSON.stringify(storeCartInfo));
    } else {
      localStorage.setItem("testCartInfo", JSON.stringify(infoObj));
    }
  };

  const [diagnosticSlot, setDiagnosticSlot] = useState<
    DiagnosticsCartContextProps["diagnosticSlot"]
  >(
    isBrowser() &&
      localStorage.getItem("testCartInfo") &&
      JSON.parse(localStorage.getItem("testCartInfo")).slotDateObj
      ? JSON.parse(localStorage.getItem("testCartInfo")).slotDateObj
      : null
  );

  const [slotDurationInMinutes, setSlotDurationInMinutes] = useState<number>(0);

  const [clinicId, setClinicId] = useState<
    DiagnosticsCartContextProps["clinicId"]
  >("");
  const [clinics, setClinics] = useState<Clinic[]>([]);

  const [prescriptions, setPrescriptions] = useState<
    DiagnosticsCartContextProps["prescriptions"]
  >(
    isBrowser() && localStorage?.getItem("diagPrescriptions")
      ? JSON.parse(localStorage?.getItem("diagPrescriptions"))
      : []
  );

  const [
    prescriptionUploaded,
    setPrescriptionUploaded,
  ] = useState<PrescriptionFormat | null>(defPresObject);

  const [ePrescriptionData, setEPrescriptionData] = useState<
    DiagnosticsCartContextProps["ePrescriptionData"]
  >([]);

  const [selectedEPrescriptionData, setSelectedEPrescriptionData] = useState<
    EPrescriptionFormat[]
  >(
    isBrowser() && localStorage?.getItem("diagSelectedEPrescriptions")
      ? JSON.parse(localStorage?.getItem("diagSelectedEPrescriptions"))
      : []
  );

  const [selectedPrescriptions, setSelectedPrescriptions] = useState<
    UploadPrescriptionType[]
  >([]);

  const [prescriptionComments, setPrescriptionComments] = useState<string>(
    isBrowser() && localStorage?.getItem("prescriptionComments")
      ? localStorage.getItem("prescriptionComments")
      : ""
  );
  const [
    selectedPatientIdForPrescription,
    setSelectedPatientIdForPrescription,
  ] = useState<string>(
    isBrowser() && localStorage?.getItem("selectedPatientIdForPrescription")
      ? localStorage.getItem("selectedPatientIdForPrescription")
      : ""
  );

  const [fallbackAreaName, setFallbackAreaName] = useState<string>("");
  const [itemLevelTATMessages, setItemLevelTATMessages] = useState<
    ItemLevelTATMessage[]
  >([]);
  const [ctaConfig, setCtaConfig] = useState<CtaConfig>(null);
  const [nonCartFlowConfig, setNonCartFlowConfig] = useState<NonCartFlowConfig>(
    null
  );

  const getUploadPrescriptionConfigFromCDN = async () => {
    const UPLOAD_PRESCRIPTION_CONFIG_URL = `${process.env.NEXT_PUBLIC_247_CDN_URL}/files/uploadPrescriptionConfig.json`;
    try {
      const { data } = await axios.get(UPLOAD_PRESCRIPTION_CONFIG_URL);
      return data?.uploadPrescriptionConfig as NonCartFlowConfig;
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const getItemBlockConfigFromCdn = async () => {
    const ITEM_BLOCK_CONFIG_URL = `${process.env.NEXT_PUBLIC_247_CDN_URL}/files/itemBlockConfig.json`;
    try {
      const { data } = await axios.get(ITEM_BLOCK_CONFIG_URL);
      return data as ItemConfiguration;
    } catch (err) {
      console.error(err);
      return {};
    }
  };

  const getCtaConfigFromCdn = async () => {
    const CTA_CONFIG_URL = `${process.env.NEXT_PUBLIC_247_CDN_URL}/files/ctaConfig.json`;
    try {
      const { data } = await axios.get(CTA_CONFIG_URL);
      return data as CtaConfig;
    } catch (err) {
      console.error(err);
      return CTA_CONFIG;
    }
  };

  useEffect(() => {
    const uploadPrescriptionConfigFromLocalStorage =
      localStorage?.getItem("uploadPrescriptionConfig") &&
      (JSON.parse(localStorage.getItem("uploadPrescriptionConfig")) as {
        data: NonCartFlowConfig;
        expiry: number;
      });

    const itemBlockConfigFromLocalStorage =
      localStorage?.getItem("itemBlockConfig") &&
      (JSON.parse(localStorage.getItem("itemBlockConfig")) as {
        data: ItemConfiguration;
        expiry: number;
      });

    const ctaConfigFromLocalStorage =
      localStorage?.getItem("ctaConfig") &&
      JSON.parse(localStorage.getItem("ctaConfig"));

    if (
      !ctaConfigFromLocalStorage ||
      new Date()?.getTime() > ctaConfigFromLocalStorage?.expiry
    ) {
      getCtaConfigFromCdn().then((configDataFromCdn) => {
        if (configDataFromCdn) {
          localStorage?.setItem(
            "ctaConfig",
            JSON.stringify({
              data: configDataFromCdn,
              expiry: new Date().getTime() + CTA_CONFIG_EXPIRY,
            })
          );
          setCtaConfig(configDataFromCdn);
        }
      });
    } else {
      setCtaConfig(ctaConfigFromLocalStorage?.data);
    }

    if (
      !itemBlockConfigFromLocalStorage ||
      new Date()?.getTime() > itemBlockConfigFromLocalStorage?.expiry
    ) {
      getItemBlockConfigFromCdn().then((configDataFromCdn) => {
        if (configDataFromCdn) {
          localStorage?.setItem(
            "itemBlockConfig",
            JSON.stringify({
              data: configDataFromCdn,
              expiry: new Date().getTime() + ITEM_CONFIG_EXPIRY,
            })
          );
          setItemConfiguration(configDataFromCdn);
        }
      });
    } else {
      setItemConfiguration(itemBlockConfigFromLocalStorage?.data);
    }

    if (
      !uploadPrescriptionConfigFromLocalStorage ||
      new Date()?.getTime() > uploadPrescriptionConfigFromLocalStorage?.expiry
    ) {
      getUploadPrescriptionConfigFromCDN().then((configDataFromCdn) => {
        if (configDataFromCdn) {
          localStorage?.setItem(
            "uploadPrescriptionConfig",
            JSON.stringify({
              data: configDataFromCdn,
              expiry: new Date().getTime() + CTA_CONFIG_EXPIRY,
            })
          );
          setNonCartFlowConfig(configDataFromCdn);
        }
      });
    } else {
      setNonCartFlowConfig(uploadPrescriptionConfigFromLocalStorage?.data);
    }
  }, []);

  useEffect(() => {
    if (isCartUpdated) {
      const items = JSON.stringify(diagnosticsCartItems);
      setIsCartUpdated(false);
      localStorage.setItem("diagnosticsCartItems", items);
    }
  }, [diagnosticsCartItems, isCartUpdated]);

  useEffect(() => {
    if (isModifyCartUpdated) {
      const items = JSON.stringify(diagnosticsModifyCartItems);
      setIsModifyCartUpdated(false);
      localStorage.setItem("diagnosticsModifyCartItems", items);
    }
  }, [diagnosticsModifyCartItems, isModifyCartUpdated]);

  useEffect(() => {
    if (isPatientItemUpdated) {
      const items = JSON.stringify(diagPatientLineItems);
      setIsPatientItemUpdated(false);
      localStorage.setItem("diagPatientLineItems", items);
    }
  }, [diagPatientLineItems, isPatientItemUpdated]);

  useEffect(() => {
    if (currentPatient && currentPatient.id && currentPatient.id.length > 0) {
      setDiagnosticsCartItems(
        localStorage.getItem(`diagnosticsCartItems`)
          ? JSON.parse(localStorage.getItem(`diagnosticsCartItems`) || "")
          : []
      );
      setDiagPatientLineItems(
        localStorage.getItem(`diagPatientLineItems`)
          ? JSON.parse(localStorage.getItem(`diagPatientLineItems`) || "")
          : []
      );
      setDiagnosticsModifyCartItems(
        localStorage.getItem(`diagnosticsModifyCartItems`)
          ? JSON.parse(localStorage.getItem(`diagnosticsModifyCartItems`) || "")
          : []
      );
    }
  }, [currentPatient]);

  useEffect(() => {
    if (prescriptionUploaded && prescriptionUploaded.name.length > 0) {
      if (prescriptions) {
        const finalPrescriptions = [...prescriptions, prescriptionUploaded];
        setPrescriptions && setPrescriptions(finalPrescriptions);
        setPrescriptionUploaded(defPresObject);
      }
    }
  }, [prescriptionUploaded, prescriptions]);

  useEffect(() => {
    localStorage.setItem(
      "circleMemberShipPlan",
      JSON.stringify(circleMemberShipPlan)
    );
  }, [circleMemberShipPlan]);

  useEffect(() => {
    // updating coupon discount here on update in cart or new coupon code applied
    const minimumOrderAmount = coupon && coupon.minimumOrderAmount;
    if (!coupon || (minimumOrderAmount && cartTotal < minimumOrderAmount)) {
      setCoupon(null);
      setCouponDiscount(0);
    } else {
      let discountAmount = 0;
      if (coupon.discountType == DiscountType.PERCENT) {
        discountAmount = parseFloat(
          ((coupon.discount / 100) * cartTotal).toFixed(2)
        );
      } else {
        discountAmount = parseFloat((cartTotal - coupon.discount).toFixed(2));
      }
      setCouponDiscount(discountAmount);
    }
  }, [cartTotal, coupon]);

  useEffect(() => {
    if (prescriptionUploaded && prescriptionUploaded.name.length > 0) {
      if (prescriptions) {
        const finalPrescriptions = [...prescriptions, prescriptionUploaded];
        setPrescriptions && setPrescriptions(finalPrescriptions);
        setPrescriptionUploaded(defPresObject);
      }
    }
  }, [prescriptionUploaded, prescriptions]);

  useEffect(() => {
    if (ePrescriptionData?.length) {
      const selectedEPrescriptions = ePrescriptionData.filter((x) => x.checked);
      setSelectedEPrescriptionData([...selectedEPrescriptions]);
    }
  }, [ePrescriptionData]);

  useEffect(() => {
    const uploadedPrescriptions: UploadPrescriptionType[] = [];

    prescriptions?.map((x) => {
      uploadedPrescriptions.push({
        id: x.name,
        name: x.fileName,
        uploadedUrl: x.imageUrl,
        forPatient: currentPatient
          ? `${currentPatient?.firstName} ${currentPatient?.lastName}`
          : "",
        fileType: x.fileType,
        date: format(new Date(), "dd MMM, yyyy"),
        dateUTC: new Date().toUTCString(),
        imageUrl: x.imageUrl,
        doctorName: "Self-uploaded Prescription",
        baseFormat: x.baseFormat,
        prescriptionType: PrescriptionType.UPLOADED_BY_USER,
      });
    });

    selectedEPrescriptionData?.map((x) => {
      uploadedPrescriptions.push({
        id: x.id,
        name:
          x.prescriptionType === PrescriptionType.UPLOADED_BY_USER
            ? x.forPatient
            : x.blobName,
        uploadedUrl: x.uploadedUrl,
        forPatient: x.forPatient,
        fileType: x.fileType,
        date: x.date,
        dateUTC: x.dateUTC,
        imageUrl: x.uploadedUrl,
        doctorName:
          x.prescriptionType === PrescriptionType.UPLOADED_BY_USER
            ? "Self-uploaded Prescription"
            : x.doctorName,
        baseFormat: null,
        prescriptionType: x.prescriptionType,
      });
    });

    setSelectedPrescriptions(uploadedPrescriptions);
  }, [selectedEPrescriptionData, prescriptions, currentPatient]);

  useEffect(() => {
    localStorage?.setItem("diagPrescriptions", JSON.stringify(prescriptions));
  }, [prescriptions]);

  useEffect(() => {
    localStorage?.setItem(
      "diagSelectedEPrescriptions",
      JSON.stringify(selectedEPrescriptionData)
    );
  }, [selectedEPrescriptionData]);

  const [openLocation, setOpenLocation] = useState<boolean>(false);
  const [showLocationPopup, setShowLocationPopup] = useState<boolean>(false);
  const [reverifySlot, setReverifySlot] = useState<boolean>(false);
  const [shouldDefault, setShouldDefault] = useState<boolean>(false);
  const [nonCartOrder, setNonCartOrder] = useState<NonCartOrder>(
    isBrowser() && localStorage.getItem("nonCartOrder")
      ? JSON.parse(localStorage.getItem("nonCartOrder"))
      : null
  );

  const [
    nonCartSuccessData,
    setNonCartSuccessData,
  ] = useState<SuccessScreenData>(
    isBrowser() && localStorage.getItem("nonCartSuccessData")
      ? JSON.parse(localStorage.getItem("nonCartSuccessData"))
      : null
  );

  useEffect(() => {
    if (isBrowser()) {
      localStorage?.setItem("nonCartOrder", JSON.stringify(nonCartOrder));
    }
  }, [nonCartOrder]);

  useEffect(() => {
    if (isBrowser()) {
      localStorage?.setItem(
        "nonCartSuccessData",
        JSON.stringify(nonCartSuccessData)
      );
    }
  }, [nonCartSuccessData]);

  return (
    <DiagnosticsCartContext.Provider
      value={{
        allowItemAddition,
        checkForCartLevelInclusionMatch,
        matchedInclusions,
        itemConfiguration,
        selectedAddressPincode,
        setSelectedAddressPincode,
        cartCityId,
        setCartCityId,
        cartArea,
        setCartArea,
        modifyOrderId,
        setModifyOrderId,
        testsTrackingItemId,
        setTestsTrackingItemId,
        testsTrackingItemName,
        setTestsTrackingItemName,
        testsTrackingStatusArr,
        setTestsTrackingStatusArr,
        forPatientId,
        setPatientId,
        prescriptions,
        setPrescriptions,
        prescriptionUploaded,
        setPrescriptionUploaded,
        diagnosticsCartItems,
        setDiagnosticsCartItems,
        diagnosticsModifyCartItems,
        setDiagnosticsModifyCartItems,
        addMultipleItemsToModifyCart,
        removeModifyCartItems,
        addModifyCartItems,
        getAllActiveCartItems,
        isItemInPrevOrder,
        undoModifyChanges,
        diagPatientLineItems,
        setDiagPatientLineItems,
        diagnosticTestTrackingItems,
        setDiagnosticTestTrackingItems,
        addCartItem,
        addPatientItems,
        addMultiPatientLineItems,
        removePatientFromCart,
        addMultipleCartItems,
        addModifyOrderId,
        removeModifyOrderId,
        removeCartItem,
        removeCartItemForPackage,
        removeUnserviseableItems,
        removePatientLineItem,
        removePatientLineItems,
        removePatientDuplicateLineItems,
        removeMultipleItemsFromCart,
        replacePackageRecommForPatient,
        updateCartItem,
        updatePatientCartLineItems,
        cartTotal,
        grandTotal,
        couponDiscount,
        deliveryAddresses,
        setDeliveryAddresses,
        deliveryAddressId,
        setDeliveryAddressId,
        deliveryType,
        coupon,
        setCoupon,
        clinicPinCode,
        setClinicPinCode,
        clinics,
        setClinics,
        clinicId,
        setClinicId,
        clearCartInfo,
        clearCartForModify,
        addCartInfo,
        diagnosticSlot,
        setDiagnosticSlot,
        slotDurationInMinutes,
        setSlotDurationInMinutes,
        setEPrescriptionData,
        ePrescriptionData,
        selectedEPrescriptionData,
        setSelectedEPrescriptionData,
        selectedPrescriptions,
        setSelectedPrescriptions,
        paidSlotCharges,
        setPaidSlotCharges,
        lastMinuteBuys,
        setLastMinuteBuys,
        lastMinuteBuysShown,
        setLastMinuteBuysShown,
        fallbackAreaName,
        setFallbackAreaName,
        itemLevelTATMessages,
        setItemLevelTATMessages,
        circleMemberShipPlan,
        setCircleMemberShipPlan,
        temporarySubscriptionId,
        setTemporarySubscriptionId,
        previouslyBookedOrder,
        setPreviouslyBookedOrder,
        autoAddressSelection,
        setAutoAddressSelection,
        diagPackageRecommendation,
        setDiagPackageRecommendation,
        diagTestRecommendation,
        setDiagTestRecommendation,
        prevDiagPackageRecommendation,
        setPrevDiagPackageRecommendation,
        previousDiagLineItems,
        setPreviousDiagLineItems,
        showUndoOption,
        setShowUndoOption,
        setPrevDiagCartItems,
        prevDiagCartItems,
        setPrevDiagPatientItems,
        prevDiagPatientItems,
        setShowCartPageUndo,
        showCartPageUndo,
        setIncludedTestsinPackage,
        includedTestsinPackage,
        sanitisePatientLineItems,
        showSearchBar,
        setShowSearchBar,
        openLocation,
        setOpenLocation,
        showLocationPopup,
        setShowLocationPopup,
        reverifySlot,
        setReverifySlot,
        resetRecommendation,
        ctaConfig,
        recentSearchItems,
        setRecentSearchItems,
        addRecentSearchItem,
        clearRecentSearchItems,
        prescriptionComments,
        setPrescriptionComments,
        selectedPatientIdForPrescription,
        setSelectedPatientIdForPrescription,
        nonCartFlowConfig: nonCartFlowConfig,
        shouldDefault,
        setShouldDefault,
        showCircleMembershipPopup,
        setShowCircleMembershipPopup,
        triggerGARemoveItemEvent,
        setShowCartRibbon,
        showCartRibbon,
        nonCartOrder,
        setNonCartOrder,
        nonCartSuccessData,
        setNonCartSuccessData,
        preOpenHcPopover,
      }}
    >
      {props.children}
    </DiagnosticsCartContext.Provider>
  );
};

const useDiagnosticsContext = () =>
  useContext<DiagnosticsCartContextProps>(DiagnosticsCartContext);

export const useDiagnosticsCart = () => {
  const diagnosticsContext = useDiagnosticsContext();
  return {
    selectedAddressPincode: diagnosticsContext.selectedAddressPincode,
    setSelectedAddressPincode: diagnosticsContext.setSelectedAddressPincode,
    cartCityId: diagnosticsContext.cartCityId,
    setCartCityId: diagnosticsContext.setCartCityId,
    cartArea: diagnosticsContext.cartArea,
    setCartArea: diagnosticsContext.setCartArea,
    prescriptions: diagnosticsContext.prescriptions,
    setPrescriptions: diagnosticsContext.setPrescriptions,
    prescriptionUploaded: diagnosticsContext.prescriptionUploaded,
    setPrescriptionUploaded: diagnosticsContext.setPrescriptionUploaded,
    modifyOrderId: diagnosticsContext.modifyOrderId,
    setModifyOrderId: diagnosticsContext.setModifyOrderId,
    testsTrackingItemId: diagnosticsContext.testsTrackingItemId,
    setTestsTrackingItemId: diagnosticsContext.setTestsTrackingItemId,
    testsTrackingItemName: diagnosticsContext.testsTrackingItemName,
    setTestsTrackingItemName: diagnosticsContext.setTestsTrackingItemName,
    testsTrackingStatusArr: diagnosticsContext.testsTrackingStatusArr,
    setTestsTrackingStatusArr: diagnosticsContext.setTestsTrackingStatusArr,
    diagnosticsCartItems: diagnosticsContext.diagnosticsCartItems,
    setDiagnosticsCartItems: diagnosticsContext.setDiagnosticsCartItems,
    diagPatientLineItems: diagnosticsContext.diagPatientLineItems,
    setDiagPatientLineItems: diagnosticsContext.setDiagPatientLineItems,
    diagnosticTestTrackingItems: diagnosticsContext.diagnosticTestTrackingItems,
    setDiagnosticTestTrackingItems:
      diagnosticsContext.setDiagnosticTestTrackingItems,
    addCartItem: diagnosticsContext.addCartItem,
    removeCartItem: diagnosticsContext.removeCartItem,
    removeCartItemForPackage: diagnosticsContext.removeCartItemForPackage,
    removeUnserviseableItems: diagnosticsContext.removeUnserviseableItems,
    removePatientLineItem: diagnosticsContext.removePatientLineItem,
    removePatientLineItems: diagnosticsContext.removePatientLineItems,
    removePatientDuplicateLineItems:
      diagnosticsContext.removePatientDuplicateLineItems,
    addPatientItems: diagnosticsContext.addPatientItems,
    addMultiPatientLineItems: diagnosticsContext.addMultiPatientLineItems,
    removePatientFromCart: diagnosticsContext.removePatientFromCart,
    removeMultipleItemsFromCart: diagnosticsContext.removeMultipleItemsFromCart,
    replacePackageRecommForPatient:
      diagnosticsContext.replacePackageRecommForPatient,
    addMultipleCartItems: diagnosticsContext.addMultipleCartItems,
    addModifyOrderId: diagnosticsContext.addModifyOrderId,
    removeModifyOrderId: diagnosticsContext.removeModifyOrderId,
    updateCartItem: diagnosticsContext.updateCartItem,
    updatePatientCartLineItems: diagnosticsContext.updatePatientCartLineItems,
    cartTotal: diagnosticsContext.cartTotal,
    grandTotal: diagnosticsContext.grandTotal,
    setDeliveryAddresses: diagnosticsContext.setDeliveryAddresses,
    deliveryAddresses: diagnosticsContext.deliveryAddresses,
    deliveryAddressId: diagnosticsContext.deliveryAddressId,
    setDeliveryAddressId: diagnosticsContext.setDeliveryAddressId,
    diagnosticSlot: diagnosticsContext.diagnosticSlot,
    setDiagnosticSlot: diagnosticsContext.setDiagnosticSlot,
    slotDurationInMinutes: diagnosticsContext.slotDurationInMinutes,
    setSlotDurationInMinutes: diagnosticsContext.setSlotDurationInMinutes,
    clinicPinCode: diagnosticsContext.clinicPinCode,
    setClinicPinCode: diagnosticsContext.setClinicPinCode,
    clinics: diagnosticsContext.clinics,
    setClinics: diagnosticsContext.setClinics,
    clinicId: diagnosticsContext.clinicId,
    setClinicId: diagnosticsContext.setClinicId,
    clearCartInfo: diagnosticsContext.clearCartInfo,
    clearCartForModify: diagnosticsContext.clearCartForModify,
    addCartInfo: diagnosticsContext.addCartInfo,
    setEPrescriptionData: diagnosticsContext.setEPrescriptionData,
    ePrescriptionData: diagnosticsContext.ePrescriptionData,
    selectedEPrescriptionData: diagnosticsContext.selectedEPrescriptionData,
    setSelectedEPrescriptionData:
      diagnosticsContext.setSelectedEPrescriptionData,
    selectedPrescriptions: diagnosticsContext.selectedPrescriptions,
    setSelectedPrescriptions: diagnosticsContext.setSelectedPrescriptions,
    paidSlotCharges: diagnosticsContext.paidSlotCharges,
    setPaidSlotCharges: diagnosticsContext.setPaidSlotCharges,
    lastMinuteBuys: diagnosticsContext.lastMinuteBuys,
    setLastMinuteBuys: diagnosticsContext.setLastMinuteBuys,
    lastMinuteBuyShown: diagnosticsContext.lastMinuteBuysShown,
    setLastMinuteBuyShown: diagnosticsContext.setLastMinuteBuysShown,
    fallbackAreaName: diagnosticsContext.fallbackAreaName,
    setFallbackAreaName: diagnosticsContext.setFallbackAreaName,
    itemLevelTATMessages: diagnosticsContext.itemLevelTATMessages,
    setItemLevelTATMessages: diagnosticsContext.setItemLevelTATMessages,
    circleMemberShipPlan: diagnosticsContext.circleMemberShipPlan,
    setCircleMemberShipPlan: diagnosticsContext.setCircleMemberShipPlan,
    temporarySubscriptionId: diagnosticsContext.temporarySubscriptionId,
    setTemporarySubscriptionId: diagnosticsContext.setTemporarySubscriptionId,
    previouslyBookedOrder: diagnosticsContext.previouslyBookedOrder,
    setPreviouslyBookedOrder: diagnosticsContext.setPreviouslyBookedOrder,
    autoAddressSelection: diagnosticsContext.autoAddressSelection,
    setAutoAddressSelection: diagnosticsContext.setAutoAddressSelection,
    diagPackageRecommendation: diagnosticsContext.diagPackageRecommendation,
    setDiagPackageRecommendation:
      diagnosticsContext.setDiagPackageRecommendation,
    diagTestRecommendation: diagnosticsContext.diagTestRecommendation,
    setDiagTestRecommendation: diagnosticsContext.setDiagTestRecommendation,
    prevDiagPackageRecommendation:
      diagnosticsContext.prevDiagPackageRecommendation,
    setPrevDiagPackageRecommendation:
      diagnosticsContext.setPrevDiagPackageRecommendation,
    previousDiagLineItems: diagnosticsContext.previousDiagLineItems,
    setPreviousDiagLineItems: diagnosticsContext.setPreviousDiagLineItems,
    showUndoOption: diagnosticsContext.showUndoOption,
    setShowUndoOption: diagnosticsContext.setShowUndoOption,
    prevDiagCartItems: diagnosticsContext.prevDiagCartItems,
    setPrevDiagCartItems: diagnosticsContext.setPrevDiagCartItems,
    prevDiagPatientItems: diagnosticsContext.prevDiagPatientItems,
    setPrevDiagPatientItems: diagnosticsContext.setPrevDiagPatientItems,
    showCartPageUndo: diagnosticsContext.showCartPageUndo,
    setShowCartPageUndo: diagnosticsContext.setShowCartPageUndo,
    includedTestsinPackage: diagnosticsContext.includedTestsinPackage,
    setIncludedTestsinPackage: diagnosticsContext.setIncludedTestsinPackage,
    sanitisePatientLineItems: diagnosticsContext.sanitisePatientLineItems,
    showSearchBar: diagnosticsContext.showSearchBar,
    setShowSearchBar: diagnosticsContext.setShowSearchBar,
    openLocation: diagnosticsContext.openLocation,
    setOpenLocation: diagnosticsContext.setOpenLocation,
    setShowLocationPopup: diagnosticsContext.setShowLocationPopup,
    showLocationPopup: diagnosticsContext.showLocationPopup,
    resetRecommendation: diagnosticsContext.resetRecommendation,
    reverifySlot: diagnosticsContext.reverifySlot,
    setReverifySlot: diagnosticsContext.setReverifySlot,
    itemConfiguration: diagnosticsContext.itemConfiguration,
    allowItemAddition: diagnosticsContext.allowItemAddition,
    checkForCartLevelInclusionMatch:
      diagnosticsContext.checkForCartLevelInclusionMatch,
    matchedInclusions: diagnosticsContext.matchedInclusions,
    ctaConfig: diagnosticsContext.ctaConfig,
    recentSearchItems: diagnosticsContext.recentSearchItems,
    setRecentSearchItems: diagnosticsContext.setRecentSearchItems,
    addRecentSearchItem: diagnosticsContext.addRecentSearchItem,
    clearRecentSearchItems: diagnosticsContext.clearRecentSearchItems,
    prescriptionComments: diagnosticsContext.prescriptionComments,
    setPrescriptionComments: diagnosticsContext.setPrescriptionComments,
    selectedPatientIdForPrescription:
      diagnosticsContext.selectedPatientIdForPrescription,
    setSelectedPatientIdForPrescription:
      diagnosticsContext.setSelectedPatientIdForPrescription,
    nonCartFlowConfig: diagnosticsContext.nonCartFlowConfig,
    shouldDefault: diagnosticsContext.shouldDefault,
    setShouldDefault: diagnosticsContext.setShouldDefault,
    showCircleMembershipPopup: diagnosticsContext.showCircleMembershipPopup,
    setShowCircleMembershipPopup:
      diagnosticsContext.setShowCircleMembershipPopup,
    diagnosticsModifyCartItems: diagnosticsContext.diagnosticsModifyCartItems,
    setDiagnosticsModifyCartItems:
      diagnosticsContext.setDiagnosticsModifyCartItems,
    addMultipleItemsToModifyCart:
      diagnosticsContext.addMultipleItemsToModifyCart,
    removeModifyCartItems: diagnosticsContext.removeModifyCartItems,
    addModifyCartItems: diagnosticsContext.addModifyCartItems,
    getAllActiveCartItems: diagnosticsContext.getAllActiveCartItems,
    isItemInPrevOrder: diagnosticsContext.isItemInPrevOrder,
    undoModifyChanges: diagnosticsContext.undoModifyChanges,
    triggerGARemoveItemEvent: diagnosticsContext.triggerGARemoveItemEvent,
    showCartRibbon: diagnosticsContext.showCartRibbon,
    setShowCartRibbon: diagnosticsContext.setShowCartRibbon,
    nonCartOrder: diagnosticsContext.nonCartOrder,
    setNonCartOrder: diagnosticsContext.setNonCartOrder,
    nonCartSuccessData: diagnosticsContext.nonCartSuccessData,
    setNonCartSuccessData: diagnosticsContext.setNonCartSuccessData,
    preOpenHcPopover: diagnosticsContext.preOpenHcPopover,
  };
};
